/**
 * NDR Automation — automated NDR rule management.
 * Live data: reads the merchant's `ndrAutomatedRule.rules` via REPORT_GENERIC_SEARCH;
 * create/activate via NDR_RULEUPDATE (status=ACTIVE), remove via NDR_RULEUPDATE
 * (status=INACTIVE), deactivate-all via NDR_RULEDEACTIVATE. No dummy data.
 * The rule-builder form (NDRCreateAutomatedRule) is reused as-is inside the drawer.
 */
import React, {useState, useEffect, useCallback} from 'react'
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Button, IconButton,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined'
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import {useSnackbar} from 'notistack'
import withLayout from '../../hocs/Layout'
import PageTitle from '../../common/PageTitle'
import Loader from '../../common/Loader'
import MUIModal from '../../common/MUIModal'
import ConfirmDialog from '../../common/ConfirmDialog'
import SideDrawer from '../weight/list/SideDrawer'
import {NDRCreateAutomatedRule} from './Automated rule/createNdrRule'
import {apiConstants, postGeneric, getGeneric} from '../../common/constants'
import {getMerchant} from '../../helpers/ReduxHelpers'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, ERROR, LIGHT_BG} from '../../styles/foundation/colors'
import {SHADOW} from '../../styles/foundation/effects'
import {pageContainerSx, pageHeroSx, heroTitleSx, heroSubSx, helpTextSx, helpTextTypeSx, primaryBtnSx} from '../../styles/patterns'

const statCardSx = (accent) => ({
  flex: '1 1 160px', minWidth: 150, bgcolor: WHITE, borderRadius: '10px', p: '16px', textAlign: 'center',
  boxShadow: SHADOW.card, border: `1px solid ${BORDER}`, borderTop: `3px solid ${accent}`,
})
const headerCellSx = {
  fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE['2xs'], color: SUBTLE,
  textTransform: 'uppercase', letterSpacing: '0.03em', py: 1.5,
}
const dataCellSx = {fontFamily: FONT, fontSize: SIZE.xs, py: 1.2, color: NAVY}

function toCamel(snake) {
  return String(snake || '').toLowerCase().replace(/([-_][a-z])/g, (g) => g.toUpperCase().replace('-', '').replace('_', ''))
}
const isRto = (rule) => rule?.result?.type === 'INITIATE_RTO'

function NDRAutomation() {
  const merchant = getMerchant()
  const merchantId = merchant?.id || ''
  const {enqueueSnackbar} = useSnackbar()

  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [reload, setReload] = useState(0)
  const [createOpen, setCreateOpen] = useState(false)
  const [viewRule, setViewRule] = useState(null)
  const [removeTarget, setRemoveTarget] = useState(null)
  const [deactivateOpen, setDeactivateOpen] = useState(false)
  const [fetched, setFetched] = useState(false)
  const [interventionConfig, setInterventionConfig] = useState(null)

  useEffect(() => {
    if (!merchantId) return
    setLoading(true)
    const payload = {
      collection: 'merchant_config', skipCount: true, countOnly: false,
      includes: 'ndrAutomatedRule',
      andfilter: [{field: 'merchantOid', operator: 'eq', value: merchantId}],
      orfilter: [],
    }
    postGeneric(apiConstants.REPORT_GENERIC_SEARCH, payload)
      .then((res) => setRules(res?.data?.data?.[0]?.ndrAutomatedRule?.rules || []))
      .catch((err) => { console.error('NDR automation fetch error:', err); setRules([]) })
      .finally(() => { setLoading(false); setFetched(true) })
    getGeneric(`intervention/rules/config`, { merchantOid: merchantId, type: 'NDR' })
      .then((res) => { setInterventionConfig(res?.data) })
      .catch(() => { setInterventionConfig(null) })
  }, [merchantId, reload])

  // Remove a single rule → NDR_RULEUPDATE with status=INACTIVE and a rebuilt payload.
  const removeRule = useCallback((rule) => {
    const payload = {ruleList: rule.name, action: isRto(rule) ? 'rto' : 'reattempt'}
    rule.conditions?.forEach((item) => {
      const key = toCamel(item.fact)
      payload[key] = item.fact === 'DAYS_SINCE_FIRST_ATTEMPT'
        ? JSON.parse(item.value).toString()
        : item.fact === 'PREFERRED_DELIVERY_DATE'
          ? item.value
          : JSON.parse(item.value).map((v) => v.toString())
    })
    postGeneric(`${apiConstants.NDR_RULEUPDATE}?merchantOid=${merchantId}&status=INACTIVE`, payload)
      .then((res) => {
        if (res.data?.status === 'SUCCESS') {
          enqueueSnackbar('Rule removed', {variant: 'success'})
          setReload((r) => r + 1)
        } else {
          enqueueSnackbar('Error while removing rule', {variant: 'error'})
        }
      })
      .catch(() => enqueueSnackbar('Some error occurred', {variant: 'error'}))
  }, [merchantId, enqueueSnackbar])

  const deactivateAll = useCallback(() => {
    postGeneric(`${apiConstants.NDR_RULEDEACTIVATE}?merchantOid=${merchantId}&status=INACTIVE`)
      .then(() => { enqueueSnackbar('Automation deactivated', {variant: 'success'}); setReload((r) => r + 1) })
      .catch(() => enqueueSnackbar('Some error occurred', {variant: 'error'}))
  }, [merchantId, enqueueSnackbar])

  const total = rules.length
  const rtoCount = rules.filter(isRto).length
  const reattemptCount = total - rtoCount
  const singleRule = total === 1

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle="Rule-based automation for non-delivery reports — auto-reattempt or auto-RTO when your conditions are met">NDR Automation</PageTitle>

      

      {loading && <Loader />}

      {/* KPI cards */}
      <Box sx={{display: 'flex', gap: '12px', mb: '18px', flexWrap: 'wrap'}}>
        <Box sx={statCardSx(PRIMARY_BLUE)}>
          <Box sx={{color: PRIMARY_BLUE, display: 'flex', justifyContent: 'center', mb: 0.5, '& svg': {fontSize: 22}}}><SmartToyOutlinedIcon /></Box>
          <Typography sx={{fontSize: '18px', fontWeight: WEIGHT.extra, color: PRIMARY_BLUE, fontFamily: FONT}}>{total}</Typography>
          <Typography sx={{color: SUBTLE, fontSize: SIZE['2xs'], mt: '2px', fontWeight: WEIGHT.medium, fontFamily: FONT}}>Active Rules</Typography>
        </Box>
        <Box sx={statCardSx(SUCCESS)}>
          <Box sx={{color: SUCCESS, display: 'flex', justifyContent: 'center', mb: 0.5, '& svg': {fontSize: 22}}}><ReplayOutlinedIcon /></Box>
          <Typography sx={{fontSize: '18px', fontWeight: WEIGHT.extra, color: SUCCESS, fontFamily: FONT}}>{reattemptCount}</Typography>
          <Typography sx={{color: SUBTLE, fontSize: SIZE['2xs'], mt: '2px', fontWeight: WEIGHT.medium, fontFamily: FONT}}>Reattempt Rules</Typography>
        </Box>
        <Box sx={statCardSx(ERROR)}>
          <Box sx={{color: ERROR, display: 'flex', justifyContent: 'center', mb: 0.5, '& svg': {fontSize: 22}}}><AssignmentReturnOutlinedIcon /></Box>
          <Typography sx={{fontSize: '18px', fontWeight: WEIGHT.extra, color: ERROR, fontFamily: FONT}}>{rtoCount}</Typography>
          <Typography sx={{color: SUBTLE, fontSize: SIZE['2xs'], mt: '2px', fontWeight: WEIGHT.medium, fontFamily: FONT}}>Auto-RTO Rules</Typography>
        </Box>
      </Box>

      {/* Intervention config status */}
      {interventionConfig && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.2,
            bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px', boxShadow: SHADOW.card,
          }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: interventionConfig.whatsappAutomatedNdr ? SUCCESS + '14' : ERROR + '14',
              color: interventionConfig.whatsappAutomatedNdr ? SUCCESS : ERROR,
            }}>
              <WhatsAppIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.2 }}>WhatsApp NDR Automation</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                {interventionConfig.whatsappAutomatedNdr
                  ? <CheckCircleOutlineIcon sx={{ fontSize: 14, color: SUCCESS }} />
                  : <CancelOutlinedIcon sx={{ fontSize: 14, color: ERROR }} />}
                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: interventionConfig.whatsappAutomatedNdr ? SUCCESS : ERROR }}>
                  {interventionConfig.whatsappAutomatedNdr ? 'Enabled' : 'Disabled'}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.2,
            bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px', boxShadow: SHADOW.card,
          }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: interventionConfig.futworksNdrConfig?.pushLead ? SUCCESS + '14' : ERROR + '14',
              color: interventionConfig.futworksNdrConfig?.pushLead ? SUCCESS : ERROR,
            }}>
              <PhoneInTalkOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.2 }}>NDR Calling</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                {interventionConfig.futworksNdrConfig?.pushLead
                  ? <CheckCircleOutlineIcon sx={{ fontSize: 14, color: SUCCESS }} />
                  : <CancelOutlinedIcon sx={{ fontSize: 14, color: ERROR }} />}
                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: interventionConfig.futworksNdrConfig?.pushLead ? SUCCESS : ERROR }}>
                  {interventionConfig.futworksNdrConfig?.pushLead ? 'Active' : 'Inactive'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={helpTextSx}>
        <InfoOutlinedIcon sx={{fontSize: 16, color: PRIMARY_BLUE, mt: 0.2}} />
        <Typography sx={helpTextTypeSx}>
          <strong>How it works:</strong> When a courier reports an NDR, the engine evaluates your rules (conditions on OFD count,
          days since first attempt, NDR reason, etc.). The first matching rule triggers its action — schedule a reattempt or initiate RTO — automatically.
        </Typography>
      </Box>

      {/* Action bar */}
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 2}}>
        <Button variant='contained' startIcon={<AddIcon />} sx={primaryBtnSx}
          onClick={() => { localStorage.removeItem('factName'); setCreateOpen(true) }}>
          Create Rule
        </Button>
        {singleRule && (
          <Button variant='outlined' size='small' color='error'
            sx={{fontFamily: FONT, textTransform: 'none', borderRadius: '8px'}}
            onClick={() => setDeactivateOpen(true)}>
            Deactivate Automation
          </Button>
        )}
      </Box>

      {/* Rules table / empty state */}
      {fetched && rules.length === 0 ? (
        <Box sx={{bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', boxShadow: SHADOW.card, p: 5, textAlign: 'center'}}>
          <Box sx={{width: 60, height: 60, borderRadius: '50%', bgcolor: LIGHT_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2, color: SUBTLE, '& svg': {fontSize: 28}}}>
            <SmartToyOutlinedIcon />
          </Box>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY}}>No automation rules yet</Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.5, maxWidth: 380, mx: 'auto'}}>
            Create your first rule to auto-handle NDRs — set conditions and choose whether to reattempt or initiate RTO.
          </Typography>
        </Box>
      ) : rules.length > 0 && (
        <TableContainer sx={{bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', boxShadow: SHADOW.card, overflow: 'hidden'}}>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{bgcolor: '#f8f9fc'}}>
                {['Rule Name', 'Conditions', 'Action', ''].map((h, i) => (
                  <TableCell key={h || i} align={i === 3 ? 'right' : 'left'} sx={headerCellSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((rule, idx) => (
                <TableRow key={rule.name || idx} sx={{'&:hover': {bgcolor: '#fafbfc'}}}>
                  <TableCell sx={{...dataCellSx, fontWeight: WEIGHT.bold}}>{rule.name}</TableCell>
                  <TableCell sx={{...dataCellSx, color: SUBTLE}}>
                    {(rule.conditions || []).map((c) => toCamel(c.fact)).join(', ') || '—'}
                  </TableCell>
                  <TableCell sx={dataCellSx}>
                    <Chip label={isRto(rule) ? 'Initiate RTO' : 'Reattempt'} size='small'
                      sx={{fontFamily: FONT, fontSize: '0.65rem', fontWeight: WEIGHT.bold,
                        color: isRto(rule) ? ERROR : SUCCESS, bgcolor: (isRto(rule) ? ERROR : SUCCESS) + '14'}} />
                  </TableCell>
                  <TableCell align='right'>
                    <IconButton size='small' onClick={() => setViewRule(rule)} sx={{color: PRIMARY_BLUE}} title='View'>
                      <VisibilityOutlinedIcon fontSize='small' />
                    </IconButton>
                    <IconButton size='small' onClick={() => setRemoveTarget(rule)} disabled={singleRule} sx={{color: singleRule ? BORDER : ERROR}} title={singleRule ? 'Use Deactivate to remove the last rule' : 'Remove'}>
                      <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create drawer — reuses the existing rule-builder form */}
      <SideDrawer title='Create New Automated Rule' subtitle='Set the conditions and the action to take'
        icon={<SmartToyOutlinedIcon />}
        open={!!createOpen} setOpen={() => setCreateOpen(false)} drawerWidth={'60%'}
        children={<NDRCreateAutomatedRule setOpen={setCreateOpen} setReload={setReload} rules={rules} />} />

      {/* View rule conditions */}
      <MUIModal open={!!viewRule} title={`Rule — ${viewRule?.name || ''}`} handleClose={() => setViewRule(null)}>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{bgcolor: '#f8f9fc'}}>
                {['Fact', 'Operator', 'Value'].map((h) => <TableCell key={h} sx={headerCellSx}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {(viewRule?.conditions || []).map((c, i) => {
                let v = c.value
                try { const p = JSON.parse(c.value); v = Array.isArray(p) ? p.join(', ') : String(p) } catch (e) { /* keep raw */ }
                return (
                  <TableRow key={i}>
                    <TableCell sx={dataCellSx}>{c.fact}</TableCell>
                    <TableCell sx={dataCellSx}>{c.operator}</TableCell>
                    <TableCell sx={dataCellSx}>{v}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mt: 1.5}}>
          Action: <strong style={{color: isRto(viewRule) ? ERROR : SUCCESS}}>{isRto(viewRule) ? 'Initiate RTO' : 'Reattempt'}</strong>
        </Typography>
      </MUIModal>

      {/* Confirms */}
      <ConfirmDialog title='' open={!!removeTarget} setOpen={() => setRemoveTarget(null)}
        onConfirm={() => removeTarget && removeRule(removeTarget)}>
        Are you sure you want to remove the rule "{removeTarget?.name}"?
      </ConfirmDialog>
      <ConfirmDialog title='' open={deactivateOpen} setOpen={setDeactivateOpen} onConfirm={deactivateAll}>
        Are you sure you want to deactivate NDR automation?
      </ConfirmDialog>
    </Box>
  )
}

export default withLayout(NDRAutomation)
